import { useState } from 'react';
import { MapLayerGroupControl } from '@/views/map/MapLayerManager/MapLayerGroupControl/MapLayerGroupControl';
import { EncroachmentLayerOption } from '@/views/map/MapLayerManager/MapLayerGroupControl/MapLayerOption/EncroachmentLayerOption';
import { TreeTypeLayerOption } from '@/views/map/MapLayerManager/MapLayerGroupControl/MapLayerOption/TreeTypeLayerOption';
import { SpeciesLayerOption } from '@/views/map/MapLayerManager/MapLayerGroupControl/MapLayerOption/SpeciesLayerOption';
import { SegmentationLayerOption } from '@/views/map/MapLayerManager/MapLayerGroupControl/MapLayerOption/SegmentationLayerOption';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { update as updateMapLayerConfig } from '@/features/mapLayerSlice';
import type { EncroachmentLayerConfig } from '@/views/map/layers/types';
import { LayerType, TREE_COVER_LAYERS } from '@/views/map/layers/types';
import { useDatasets } from '@/hooks/useDatasets';
import { OVERHANG_LAYER_TYPE } from '@/views/map/layers/encroachment';
import { isTreeCoverActive } from '@/views/map/utils';
import { hasLayer } from './utils';

export const TreeCoverControl = () => {
  const dispatch = useAppDispatch();
  const layerConfig = useAppSelector((s) => s.mapLayer);
  const { datasets } = useDatasets();

  // Get the last selected tree cover from LayerManager scope
  const [lastSelectedTreeCover, setLastSelectedTreeCover] = useState(
    LayerType[
      TREE_COVER_LAYERS.find(
        (l) => hasLayer(datasets, l) && layerConfig[l].visible
      ) ??
        TREE_COVER_LAYERS.find((l) => hasLayer(datasets, l)) ??
        LayerType.encroachment
    ]
  );

  const selected = lastSelectedTreeCover;
  const isActive = isTreeCoverActive(layerConfig);
  const showOverhang = (
    layerConfig[LayerType.encroachment] as EncroachmentLayerConfig
  ).showOverhang;

  const updateState = (isVisible: (z: string) => boolean) => {
    dispatch(
      updateMapLayerConfig({
        [LayerType.segmentation]: { visible: isVisible('segmentation') },
        [LayerType.encroachment]: { visible: isVisible('encroachment') },
        [LayerType.tree_type]: { visible: isVisible('tree_type') },
        [LayerType.tree_species]: { visible: isVisible('tree_species') },
      })
    );
  };

  const onOverhangChange = hasLayer(datasets, OVERHANG_LAYER_TYPE)
    ? (v: boolean) =>
        dispatch(
          updateMapLayerConfig({
            [LayerType.encroachment]: {
              ...layerConfig[LayerType.encroachment],
              showOverhang: v,
            },
          })
        )
    : () => {};

  const setTreeCoverViz = (v: boolean) =>
    updateState((selectedType: string) => v && selected === selectedType);
  const setOption = (v: string) => {
    // Save the existing selection, to return to it upon re-activating the toggle
    setLastSelectedTreeCover(v);
    updateState((selectedType: string) => isActive && v === selectedType);
  };

  return (
    <MapLayerGroupControl
      title="Tree cover"
      description="Trees detected by our algorithms"
      onVisibilityChange={setTreeCoverViz}
      isGroupVisible={isActive}
      data-testid="tree-cover-control"
    >
      {hasLayer(datasets, LayerType.segmentation) && (
        <SegmentationLayerOption selected={selected} onChange={setOption} />
      )}
      {hasLayer(datasets, LayerType.encroachment) && (
        <EncroachmentLayerOption
          selected={selected}
          onChange={setOption}
          hasOverhang={hasLayer(datasets, OVERHANG_LAYER_TYPE)}
          showOverhang={showOverhang}
          onOverhangChange={onOverhangChange}
        />
      )}
      {hasLayer(datasets, LayerType.tree_type) &&
        !hasLayer(datasets, LayerType.tree_species) && (
          <TreeTypeLayerOption selected={selected} onChange={setOption} />
        )}
      {hasLayer(datasets, LayerType.tree_species) && (
        <SpeciesLayerOption selected={selected} onChange={setOption} />
      )}
    </MapLayerGroupControl>
  );
};
